import React, { useCallback, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import type { ResolverAttributeEntry, ResolverResponse, StatelessResolverRequest } from "../../../../types/resolver";
import { resolveResolverStateless } from "../../../../utils/api";
import type { CodeBuilderEntry, KeyValueEntry, ResolverCodeType } from "./utils";
import { RESOLVER_CODE_TYPE_OPTIONS } from "./utils";
import { QuestionSearchAttributeAdder } from "./QuestionSearchAttributeAdder";

const WORK_ITEM_FIELD_NAMES = [
    "patientClass",
    "visitReason",
    "serviceLine",
    "healthSystemTenantId",
    "healthSystemCode",
    "insurance",
    "caseCategory",
    "examDescription",
    "patientName",
    "patientGender",
    "hospitalAccountRecord",
    "facility",
    "referringProviderName",
    "performingProviderName",
    "billingProviderName",
    "caseId",
    "caseJsonId",
    "tenantUid",
    "processInstanceGuid",
    "caseRunUid",
    "ruleId",
    "ruleName",
    "ruleCollectionId",
    "matchedRuleCriteria",
    "matchedRuleValue",
    "importId",
] as const;

export interface ResolverTesterSectionProps {
    codes: CodeBuilderEntry[];
    addCode: () => void;
    removeCode: (id: string) => void;
    moveCode: (id: string, direction: "up" | "down") => void;
    updateCode: (id: string, patch: Partial<CodeBuilderEntry>) => void;
    addCodeAttributeEntry: (codeId: string, entry: ResolverAttributeEntry) => void;
    removeCodeAttributeEntry: (codeId: string, attributeId: string) => void;
    updateCodeAttributeEntry: (
        codeId: string,
        attributeId: string,
        patch: Partial<ResolverAttributeEntry>
    ) => void;
    caseAttributes: ResolverAttributeEntry[];
    addCaseAttributeEntry: (entry: ResolverAttributeEntry) => void;
    removeCaseAttributeEntry: (attributeId: string) => void;
    updateCaseAttributeEntry: (attributeId: string, patch: Partial<ResolverAttributeEntry>) => void;
    workItemDataEntries: KeyValueEntry[];
    addWorkItemField: () => void;
    removeWorkItemField: (id: string) => void;
    updateWorkItemField: (id: string, patch: Partial<KeyValueEntry>) => void;
    availableWorkItemFields: string[];
    generatedInput: unknown;
    generatedConfig: unknown;
    onPasteInput: () => void;
    onCopyInput: () => void;
}

export function ResolverTesterSection({
    codes,
    addCode,
    removeCode,
    moveCode,
    updateCode,
    addCodeAttributeEntry,
    removeCodeAttributeEntry,
    updateCodeAttributeEntry,
    caseAttributes,
    addCaseAttributeEntry,
    removeCaseAttributeEntry,
    updateCaseAttributeEntry,
    workItemDataEntries,
    addWorkItemField,
    removeWorkItemField,
    updateWorkItemField,
    availableWorkItemFields,
    generatedInput,
    generatedConfig,
    onPasteInput,
    onCopyInput,
}: ResolverTesterSectionProps) {
    const [response, setResponse] = useState<ResolverResponse | null>(null);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [payloadPreviewTab, setPayloadPreviewTab] = useState<"input" | "resolverConfig" | "response">("input");
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const payloadPreviewRef = useRef<HTMLDivElement>(null);

    const runResolver = useCallback(async () => {
        setIsRunning(true);
        setRequestError(null);
        try {
            const payload = {
                resolverConfig: generatedConfig,
                input: generatedInput,
            };
            const resolverResponse = await resolveResolverStateless(payload as StatelessResolverRequest);
            setResponse(resolverResponse);
            setPayloadPreviewTab("response");
            queueMicrotask(() => {
                const el = payloadPreviewRef.current;
                if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : "Resolver request failed";
            setRequestError(message);
        } finally {
            setIsRunning(false);
        }
    }, [generatedConfig, generatedInput]);

    const getPayloadText = useCallback((): string => {
        if (payloadPreviewTab === "input") return JSON.stringify(generatedInput, null, 2);
        if (payloadPreviewTab === "resolverConfig") return JSON.stringify(generatedConfig, null, 2);
        return response ? JSON.stringify(response, null, 2) : "Run resolver to see output";
    }, [generatedConfig, generatedInput, payloadPreviewTab, response]);

    const payloadText = getPayloadText();

    const matchCount = useMemo(() => {
        const term = searchTerm.trim();
        if (!term) return 0;
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const matches = payloadText.match(new RegExp(escaped, "gi"));
        return matches?.length ?? 0;
    }, [payloadText, searchTerm]);

    const handleCopyCurrentTab = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(payloadText);
        } catch {
            // no-op for now; keep UI simple and non-blocking
        }
    }, [payloadText]);

    const renderPreviewContent = useCallback(() => {
        const term = searchTerm.trim();
        if (!term) return payloadText;
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(${escaped})`, "gi");
        const pieces = payloadText.split(regex);
        const termLower = term.toLowerCase();
        return pieces.map((piece, idx) =>
            piece.toLowerCase() === termLower ? (
                <Box key={`${piece}-${idx}`} component="mark" sx={{ backgroundColor: "warning.light", px: 0.25 }}>
                    {piece}
                </Box>
            ) : (
                <React.Fragment key={`${piece}-${idx}`}>{piece}</React.Fragment>
            )
        );
    }, [payloadText, searchTerm]);

    return (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="resolver-tester-section">
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Resolver Tester
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Input Builder</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" size="small" onClick={onPasteInput} data-testid="paste-input-button">
                                Paste input
                            </Button>
                            <Button variant="outlined" size="small" onClick={onCopyInput} data-testid="copy-input-button">
                                Copy input
                            </Button>
                        </Stack>
                    </Stack>
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Codes</Typography>
                            <Button variant="outlined" size="small" onClick={addCode}>
                                Add Code
                            </Button>
                        </Stack>
                        {codes.map((code, index) => (
                            <Paper key={code.id} variant="outlined" sx={{ p: 1 }}>
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1}>
                                        <FormControl size="small" sx={{ minWidth: 125 }}>
                                            <InputLabel id={`${code.id}-type-label`}>Type</InputLabel>
                                            <Select
                                                labelId={`${code.id}-type-label`}
                                                label="Type"
                                                value={code.codeType}
                                                onChange={(event) =>
                                                    updateCode(code.id, { codeType: event.target.value as ResolverCodeType })
                                                }
                                            >
                                                {RESOLVER_CODE_TYPE_OPTIONS.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            size="small"
                                            label="Code"
                                            value={code.code}
                                            onChange={(event) => updateCode(code.id, { code: event.target.value })}
                                            sx={{ flexGrow: 1 }}
                                        />
                                        <Button color="error" onClick={() => removeCode(code.id)}>
                                            Remove
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => moveCode(code.id, "up")}
                                            disabled={index === 0}
                                            aria-label={`Move code ${code.code || index + 1} up`}
                                        >
                                            Up
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => moveCode(code.id, "down")}
                                            disabled={index >= codes.length - 1}
                                            aria-label={`Move code ${code.code || index + 1} down`}
                                        >
                                            Down
                                        </Button>
                                    </Stack>
                                    {code.codeType === "PROCEDURE" && (
                                        <TextField
                                            size="small"
                                            label="Units"
                                            value={code.units}
                                            onChange={(event) => updateCode(code.id, { units: event.target.value })}
                                        />
                                    )}
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="caption">Code Attributes</Typography>
                                    </Stack>
                                    {code.attributes.map((attribute) => (
                                        <Box
                                            key={attribute.id}
                                            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {attribute.questionLabel}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {attribute.answerOptions && attribute.answerOptions.length > 0 ? (
                                                    <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                                                        <InputLabel id={`attr-${attribute.id}-label`}>Answer</InputLabel>
                                                        <Select
                                                            labelId={`attr-${attribute.id}-label`}
                                                            label="Answer"
                                                            value={attribute.answerValueId ?? ""}
                                                            onChange={(e) =>
                                                                updateCodeAttributeEntry(code.id, attribute.id, {
                                                                    answerValueId: Number(e.target.value),
                                                                })
                                                            }
                                                            data-testid={`code-attribute-answer-${attribute.id}`}
                                                        >
                                                            {attribute.answerOptions.map((opt) => (
                                                                <MenuItem key={opt.id} value={opt.id}>
                                                                    {opt.displayText}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <TextField
                                                        size="small"
                                                        label="Answer"
                                                        value={attribute.answerDisplayText ?? ""}
                                                        onChange={(e) =>
                                                            updateCodeAttributeEntry(code.id, attribute.id, {
                                                                answerDisplayText: e.target.value,
                                                            })
                                                        }
                                                        fullWidth
                                                        sx={{ flex: 1 }}
                                                        data-testid={`code-attribute-freetext-${attribute.id}`}
                                                    />
                                                )}
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => removeCodeAttributeEntry(code.id, attribute.id)}
                                                    aria-label={`Remove attribute ${attribute.questionLabel}`}
                                                >
                                                    Remove
                                                </Button>
                                            </Stack>
                                        </Box>
                                    ))}
                                    <QuestionSearchAttributeAdder
                                        onAdd={(entry) => addCodeAttributeEntry(code.id, entry)}
                                        existingQuestionIds={code.attributes.map((a) => a.questionId)}
                                        label="Add code attribute"
                                    />
                                </Stack>
                            </Paper>
                        ))}

                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                            <Typography variant="body2">Case Attributes</Typography>
                        </Stack>
                        {caseAttributes.map((attribute) => (
                            <Box
                                key={attribute.id}
                                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {attribute.questionLabel}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {attribute.answerOptions && attribute.answerOptions.length > 0 ? (
                                        <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                                            <InputLabel id={`case-attr-${attribute.id}-label`}>Answer</InputLabel>
                                            <Select
                                                labelId={`case-attr-${attribute.id}-label`}
                                                label="Answer"
                                                value={attribute.answerValueId ?? ""}
                                                onChange={(e) =>
                                                    updateCaseAttributeEntry(attribute.id, {
                                                        answerValueId: Number(e.target.value),
                                                    })
                                                }
                                                data-testid={`case-attribute-answer-${attribute.id}`}
                                            >
                                                {attribute.answerOptions.map((opt) => (
                                                    <MenuItem key={opt.id} value={opt.id}>
                                                        {opt.displayText}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            size="small"
                                            label="Answer"
                                            value={attribute.answerDisplayText ?? ""}
                                            onChange={(e) =>
                                                updateCaseAttributeEntry(attribute.id, {
                                                    answerDisplayText: e.target.value,
                                                })
                                            }
                                            fullWidth
                                            sx={{ flex: 1 }}
                                            data-testid={`case-attribute-freetext-${attribute.id}`}
                                        />
                                    )}
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => removeCaseAttributeEntry(attribute.id)}
                                        aria-label={`Remove case attribute ${attribute.questionLabel}`}
                                    >
                                        Remove
                                    </Button>
                                </Stack>
                            </Box>
                        ))}
                        <QuestionSearchAttributeAdder
                            onAdd={addCaseAttributeEntry}
                            existingQuestionIds={caseAttributes.map((a) => a.questionId)}
                            label="Add case attribute"
                        />

                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                            <Typography variant="body2">Work Item Data</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={addWorkItemField}
                                disabled={availableWorkItemFields.length === 0}
                                aria-label="Add work item field"
                            >
                                Add Field
                            </Button>
                        </Stack>
                        {workItemDataEntries.map((entry) => {
                            const fieldOptions = WORK_ITEM_FIELD_NAMES.filter(
                                (name) =>
                                    name === entry.key ||
                                    !workItemDataEntries.some((e) => e.id !== entry.id && e.key === name)
                            );
                            return (
                                <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                                    <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                                        <InputLabel id={`workitem-${entry.id}-field-label`}>Field</InputLabel>
                                        <Select
                                            labelId={`workitem-${entry.id}-field-label`}
                                            label="Field"
                                            value={
                                                fieldOptions.includes(
                                                    entry.key as (typeof WORK_ITEM_FIELD_NAMES)[number]
                                                )
                                                    ? entry.key
                                                    : fieldOptions[0] ?? ""
                                            }
                                            onChange={(event) =>
                                                updateWorkItemField(entry.id, { key: event.target.value })
                                            }
                                            data-testid={`workitem-field-${entry.id}`}
                                        >
                                            {fieldOptions.map((name) => (
                                                <MenuItem key={name} value={name}>
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Value"
                                        value={entry.value}
                                        onChange={(event) =>
                                            updateWorkItemField(entry.id, { value: event.target.value })
                                        }
                                        sx={{ flex: 1 }}
                                        placeholder="e.g. INPATIENT, SCREENING"
                                    />
                                    <Button color="error" onClick={() => removeWorkItemField(entry.id)}>
                                        Remove
                                    </Button>
                                </Stack>
                            );
                        })}
                    </Stack>
                    <Button variant="contained" onClick={runResolver} disabled={isRunning} sx={{ mt: 2 }}>
                        {isRunning ? "Running..." : "Run Resolver"}
                    </Button>
                    {requestError && (
                        <Alert sx={{ mt: 2 }} severity="error">
                            {requestError}
                        </Alert>
                    )}
                </Grid>
                <Grid item xs={12} md={7}>
                    <Box ref={payloadPreviewRef} data-testid="resolver-payload-preview">
                    <Typography variant="subtitle2">Payload Preview</Typography>
                    <Tabs
                        value={payloadPreviewTab}
                        onChange={(_event, value) => setPayloadPreviewTab(value)}
                        sx={{ minHeight: 36, mt: 0.5 }}
                    >
                        <Tab label="Input" value="input" />
                        <Tab label="Resolver Config" value="resolverConfig" />
                        <Tab label="Response" value="response" />
                    </Tabs>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button size="small" variant="outlined" onClick={handleCopyCurrentTab}>
                            Copy current tab
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => setSearchDialogOpen(true)}>
                            Search
                        </Button>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Box
                        component="pre"
                        sx={{ fontSize: 12, whiteSpace: "pre-wrap", maxHeight: 340, overflow: "auto" }}
                    >
                        {renderPreviewContent()}
                    </Box>
                    </Box>
                </Grid>
            </Grid>
            <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Search payload preview</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Find text"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        helperText={searchTerm.trim() ? `${matchCount} matches in current tab` : "Enter text to highlight matches"}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSearchDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
